import React, { useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useClasseContext } from '../context/ClasseContext';
import { useUser } from '../context/UserContext';
import Loading from '../components/general/Loading';
/**
 * `scopeSeance` (routes /live et /projection uniquement) : l'URL de séance
 * porte un contexte ambigu (:idclasse = classe OU groupe, deux tables), mais
 * c'est la SÉANCE chargée qui tranche (contrat back : classe XOR groupe) —
 * pas les marqueurs localStorage. Dans ce mode on valide l'appartenance
 * contre l'UNION classes + groupes du prof, sans exiger de marqueur : les
 * accès directs (popup d'accueil, rappel local, lien de secours) passent.
 * La vraie barrière reste le back : SessionQCMViewSet filtre par
 * `professeur__user=user` (vérifié 2026-09-01) — une séance d'autrui = 404.
 */
const PrivateRoute = ({ children, scopeSeance = false }) => {
  const { idclasse } = useParams(); // Récupérer l'id de la classe depuis l'URL
  const { classeNom, groupNom, setClasseNom, setGroupNom } = useClasseContext();
  const { user, isAuthenticated, loading, etablissementData, etablissementLoading , etablissementError} = useUser();

  // Vérifie si l'utilisateur est authentifié
 
  const isProfesseur = user?.role === 'professeur'; // Vérifie si l'utilisateur est un professeur
  const isChef = user?.role === "chef"; 

  
  // Utiliser useMemo pour calculer validClasseIds
  const validClasseIds = useMemo(() => {
    let ids = [];

    if (isProfesseur && etablissementData) {
      // Récupérer les classes du professeur
      const userId = parseInt(localStorage.getItem('iduser'), 10); // On récupère l'ID USER dans le localStorage.

      const professeur = etablissementData?.professeurs?.find(prof => prof.id === userId);
      if (scopeSeance) {
        // Mode séance : l'id doit appartenir au prof, classe OU groupe —
        // la séance chargée dira laquelle, le marqueur n'a pas voix ici.
        ids = [...(professeur?.classes || []), ...(professeur?.groupes || [])];
      } else {
        // Vérifier groupNom OU localStorage (pour les onglets ouverts via window.open)
        const isGroupe = groupNom || localStorage.getItem('groupeActive');
        if (isGroupe) {
          // Vérification des groupes du professeur
          ids = professeur?.groupes || [];
        } else {
          // Vérification des classes du professeur
          ids = professeur?.classes || [];
        }
      }
    } else if (isChef && etablissementData) {
      // Pour le chef, toutes les classes de l'établissement sont valides
      ids = etablissementData?.classes?.map(classe => classe.id) || [];
    }

    return ids;
  }, [isProfesseur, isChef, etablissementData, groupNom, scopeSeance]);

  // Utiliser un useEffect pour effectuer les mises à jour d'état après le rendu
  useEffect(() => {
    // Si l'utilisateur n'est pas authentifié ou n'est ni professeur ni chef, on réinitialise le contexte.
    if (!isAuthenticated || (!isProfesseur && !isChef)) {
      setClasseNom("");
      setGroupNom("");
    }
    // Si idclasse est présent, on vérifie sa validité et on met à jour le contexte en conséquence.
    if (idclasse) {
      if (
        !validClasseIds.includes(parseInt(idclasse, 10)) ||
        (!scopeSeance && !localStorage.getItem('classeActive') && !localStorage.getItem('groupeActive'))
      ) {
        setClasseNom("");
        setGroupNom("");
      } else if (localStorage.getItem('classeActive')) {
        setClasseNom(localStorage.getItem('classeActive'));
      } else {
        setGroupNom(localStorage.getItem('groupeActive'));
      }
    }
  }, [isAuthenticated, isProfesseur, isChef, idclasse, validClasseIds, setClasseNom, setGroupNom]);

      // Gestion du chargement ET de l'erreur
if (loading || etablissementLoading || (user && !etablissementData && !etablissementError)) {
  return (
          <div className="flex items-center justify-center min-h-screen bg-sky-100">
            <Loading loadingText={"On charge..."} />
          </div>
        );
      }

// Si erreur de chargement des données établissement
if (etablissementError) {
  return <Navigate to="/" />; // ou afficher un message d'erreur
}

  // Maintenant, dans le rendu, on peut décider de rediriger sans mettre à jour l'état
  if (!isAuthenticated || (!isProfesseur && !isChef)) {

    return <Navigate to="/" />;
  }

  if (idclasse && !loading && !etablissementLoading) {
 
    if (
      !validClasseIds.includes(parseInt(idclasse, 10)) ||
      (!scopeSeance && !localStorage.getItem('classeActive') && !localStorage.getItem('groupeActive'))
    ) {

      return <Navigate to="/" />;
    }
  }
  
  return children; // Si tout est bon, retourne les enfants (les composants enveloppés)
};

export default PrivateRoute;